import React from 'react';
import PropTypes from 'prop-types';
import {Map, List} from 'immutable';
import uuid from 'uuid/v4';
import {callTargetedAction, setUpStore} from 'client/framework';
import TextArea from 'generic/Forms/TextArea/TextArea.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import IconTooltip from 'sg/IconTooltip/IconTooltip.react';
import Button from 'sg/Button/Button.react';
import SnippetSelectionQuestionStore from './SnippetSelectionQuestion.store';
import snippetSelectionQuestionActions from './SnippetSelectionQuestion.actions';
import {AuthoringQuestionModelV1} from 'resources/augmented/AuthoringQuestion/AuthoringQuestionModel.v1';

function getSnippetsInPrompt(prompt) {
  return prompt.match(/{{(.*?)}}/g);
}

function formatSnippets(snippets) {
  return snippets.map((option) => option.slice(2, -2));
}

function createOptions(optionsArray) {
  return List(
    optionsArray.map((option) => {
      return Map({
        label: '',
        value: option,
        id: uuid()
      });
    })
  );
}

export default class SnippetSelectionQuestionEditor extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onModelChange: PropTypes.func,
    validationErrors: PropTypes.instanceOf(Map)
  };

  render() {
    return (
      <div className='multiplechoice-editor'>
        <SnippetSelectionPrompt
          question={this.props.question}
          onModelChange={this.props.onModelChange}
          validationErrors={this.props.validationErrors}
        />
      </div>
    );
  }
}

class SnippetSelectionPrompt extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onModelChange: PropTypes.func,
    validationErrors: PropTypes.instanceOf(Map)
  };

  constructor(props) {
    super(props);
    this.setSelectedOption(props.question);
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (nextProps.question.getId() && this.getStore().questionId !== nextProps.question.getId()) {
      this.setSelectedOption(nextProps.question);
    }
  }

  getStore() {
    return setUpStore(
      SnippetSelectionQuestionStore,
      'SnippetSelectionEditorStore',
      this.props.question.getId()
    );
  }

  setSelectedOption(question) {
    const targetStore = this.getStore().getName();
    callTargetedAction({
      name: snippetSelectionQuestionActions.SET_SELECTED_OPTIONS,
      targetStore,
      payload: question
        .getValidResponse()
        .keySeq()
        .toSet()
    });
  }

  handleOptionsMismatch(prompt) {
    const question = this.props.question;
    const snippetsInPrompt = getSnippetsInPrompt(prompt);
    if (!snippetsInPrompt) {
      return question;
    }
    const currentOptions = question.getOptions();
    if (!currentOptions.isEmpty() && !snippetsInPrompt) {
      // If our current options are not empty and we have no snippets, clear the options in our question
      return this.updateOptions(0, []);
    }
    const formattedSnippets = formatSnippets(snippetsInPrompt);

    let firstIndexWithMismatch;
    if (currentOptions.isEmpty()) {
      firstIndexWithMismatch = 0;
    } else if (snippetsInPrompt.length >= currentOptions.size) {
      firstIndexWithMismatch = formattedSnippets.findIndex((option, i) => {
        return !currentOptions.get(i) || option !== currentOptions.get(i).getValue();
      });
    } else {
      firstIndexWithMismatch = currentOptions.findIndex(
        (option, i) => option.getValue() !== formattedSnippets[i]
      );
    }

    if (firstIndexWithMismatch !== -1) {
      return this.updateOptions(
        firstIndexWithMismatch,
        formattedSnippets.slice(firstIndexWithMismatch)
      );
    }

    return question;
  }

  updateOptions(startIndex, snippets) {
    const snippetOptions = createOptions(snippets);
    const updatedOptions = this.props.question
      .getOptions()
      .take(startIndex)
      .concat(snippetOptions);
    const updatedValidResponses = this.props.question
      .getValidResponse()
      .filter((val, key) => updatedOptions.some((option) => option.get('id') === key));
    return this.props.question.setOptions(updatedOptions).setValidResponse(updatedValidResponses);
  }

  updatePrompt = (e, prompt) => {
    if (!prompt) {
      prompt = e.target.value;
    }

    const questionWithUpdatedOptions = this.handleOptionsMismatch(prompt);
    const updatedQuestion = questionWithUpdatedOptions.setSnippetPrompt(prompt);
    this.props.onModelChange(updatedQuestion, 'snippetPrompt', 'options');
  };

  handleOptionClick = (optionId) => {
    const currentValidResponses = this.props.question.getValidResponse();
    let updatedValidResponses;
    if (currentValidResponses.has(optionId)) {
      updatedValidResponses = currentValidResponses.delete(optionId);
    } else {
      updatedValidResponses = currentValidResponses.set(optionId, true);
    }

    const updatedQuestion = this.props.question.setValidResponse(updatedValidResponses);

    this.props.onModelChange(updatedQuestion, 'rubric');

    callTargetedAction({
      name: snippetSelectionQuestionActions.TOGGLE_OPTION,
      targetStore: this.getStore().getName(),
      payload: optionId
    });
  };

  snippetEverything = () => {
    // Kill all {{ and }}
    const promptSansSnippets = this.props.question
      .getSnippetPrompt()
      .replace(/[(?:{{)(?:}})]/g, '');
    // Wraps everything in the prompt that is not one of the
    // following (, . ; : ? ! ( ) — [ ] ") in curly braces.
    // This is a pretty loose temporary solution. Will revisit.
    const snippetedPrompt = promptSansSnippets.replace(/([^\s,.;:?!()—[\]"]+)/g, '{{$&}}');

    this.updatePrompt(null, snippetedPrompt);
  };

  render() {
    const snippetLabel = (
      <div>
        Snippet Text
        <IconTooltip
          content={`Enter the text for the interactive snippet
            selection area here. Wrap available snippet options
            in double curly braces {{like this}}.`}
        />
      </div>
    );

    const snippetPromptError = this.props.validationErrors.get('snippetPrompt') ? (
      <div className='a-form-input__error'>{this.props.validationErrors.get('snippetPrompt')}</div>
    ) : null;

    const snippetOptionsError = this.props.validationErrors.get('options') ? (
      <div className='a-form-input__error'>{this.props.validationErrors.get('options')}</div>
    ) : null;
    return (
      <div className='snippet-selection-editor'>
        <TextArea
          className='question-editor__question-detail--fullwidth'
          label={snippetLabel}
          onChange={this.updatePrompt}
          value={this.props.question.getSnippetPrompt()}
        />
        {snippetPromptError}
        {snippetOptionsError}
        <div className='snippet-selection-editor__snippet-everything-wrapper'>
          <Button onClick={this.snippetEverything} text='Convert all words to snippets' />
        </div>
        <div className='snippet-selection-question'>
          <label className='a-form-input__label'>
            Correct Options
            <IconTooltip
              content={`Select the correct snippet options.
                Your question must have at least one
                correct option.`}
            />
          </label>
          <MarkdownRendererV2
            text={this.props.question.getSnippetPrompt()}
            snippetSelectionData={{
              options: this.props.question.getOptions(),
              storeName: 'SnippetSelectionEditorStore',
              onOptionClick: this.handleOptionClick
            }}
          />
          {this.props.validationErrors.get('rubric') ? (
            <div className='a-form-input__error'>{this.props.validationErrors.get('rubric')}</div>
          ) : null}
        </div>
      </div>
    );
  }
}
